import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { colors, midas } from '@resurs/midas'
import { getText } from '../../internal/TextLabels'
import AppIcon from '../../internal/AppIcon'
import Tooltip from '../../internal/Tooltip'
import Badge from '../../internal/Badge'
import { classNames } from '../../util/helpers'

import styles from './DataTable.module.scss'

const caret = ({ id, nonSortable }, lastSort) =>
  !nonSortable &&
  id === lastSort.headerName && (
    <i
      style={{
        transform: lastSort.direction === 'desc' && 'rotate(180deg)',
      }}
      className={[midas.icon, midas.iconCaret, midas.ml10].join(' ')}
    />
  )

const TableHeader = ({ header, lastSort, setLastSort }) => {
  const handleHeaderClick = e => {
    if (header.nonSortable) {
      return
    }

    const { id: currentHeaderName } = e.target
    const { headerName: previousHeaderName, direction: previousDirection } =
      lastSort
    const currentDirection =
      previousHeaderName === currentHeaderName && previousDirection === 'asc'
        ? 'desc'
        : 'asc'

    setLastSort({
      headerName: currentHeaderName,
      direction: currentDirection,
    })
  }

  return (
    <th
      title={header.label}
      tabIndex={0}
      key={header.id}
      id={header.id}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: header.nonSortable ? 'default' : 'pointer',
        textAlign:
          (!header.type && 'left') ||
          (header.type === 'number' && 'right') ||
          (header.type === 'date' && 'right') ||
          (header.type === 'icon' && 'center'),
        paddingRight: header.type === 'number' && '2rem',
        color: header.color,
      }}
      onClick={handleHeaderClick}
    >
      {header.tooltip ? (
        <div
          style={{
            display: 'flex',
            flexFlow: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div
            key={header.id}
            id={header.id}
            role="columnheader"
            style={{ marginRight: '1rem' }}
            onClick={handleHeaderClick}
            onKeyPress={undefined}
            tabIndex={-1}
          >
            {header.label} {caret(header, lastSort)}
          </div>
          <Tooltip content={header.tooltip}>
            <Badge text="?" />
          </Tooltip>
        </div>
      ) : (
        <>
          {header.label} {caret(header, lastSort)}
        </>
      )}
    </th>
  )
}

const DataTable = props => {
  const [lastSort, setLastSort] = useState({
    headerName: props.lastSort?.headerName || '',
    direction: props.lastSort?.direction || '',
  })
  const [searchTerm, setSearchTerm] = useState(props.searchTerm)
  const [selectedValue, setSelectedValue] = useState(
    props.actionModel && props.actionModel.default,
  )
  const [checkedRows, setCheckedRows] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const [headerCheckbox, setHeaderCheckbox] = useState('none')
  const [activeAction, setActiveAction] = useState(false)

  const listSize = [5, 10, 20, 50]

  const getChildrenLength = () => {
    if (props.children.props.children.length < props.pagination.rowsPerPage) {
      return props.children.props.children.length
    }
    return props.pagination.rowsPerPage
  }

  const handleSearch = e => {
    e.preventDefault()
    props.onSearch(searchTerm)
  }

  const clearCheckedRows = () => {
    setHeaderCheckbox('none')
    setCheckedRows([])
    setSelectedKeys([])
  }

  const handleToggleAllRows = () => {
    if (headerCheckbox === 'none' || headerCheckbox === 'indeterminate') {
      const rowsLength = getChildrenLength()
      const tSelectedKeys = props.children.props.children
        .filter(item => item.props['data-checkbox-disabled'] !== 'true')
        .map(item => item.key)

      const tCheckedRows = []
      for (let i = 0; i < rowsLength; i++) {
        if (
          props.children.props.children[i].props['data-checkbox-disabled'] !==
          'true'
        ) {
          tCheckedRows.push(i)
        }
      }
      setHeaderCheckbox('all')
      setCheckedRows(tCheckedRows)
      setSelectedKeys(tSelectedKeys)
    }

    if (headerCheckbox === 'all') {
      clearCheckedRows()
    }
  }

  const handleRowSelect = (index, rowKey) => {
    const rowsLength = getChildrenLength()
    const exists = checkedRows.includes(index)

    const tCheckedRows = exists
      ? checkedRows.filter(val => val !== index)
      : Array.from(new Set([...checkedRows, index]))

    const tSelectedKeys = exists
      ? selectedKeys.filter(key => key !== rowKey)
      : Array.from(new Set([...selectedKeys, rowKey]))

    const tHeaderCheckbox =
      checkedRows.length > 0
        ? checkedRows === rowsLength
          ? 'all'
          : 'indeterminate'
        : 'none'

    setCheckedRows(tCheckedRows)
    setSelectedKeys(tSelectedKeys)
    setHeaderCheckbox(tHeaderCheckbox)
  }

  const handleActionSelectorChange = e => {
    if (e.target.value !== props.actionModel.default) {
      props.onActionSelectorChange({
        action: e.target.value,
        keys: selectedKeys,
      })

      setSelectedValue(props.actionModel.default)
      setActiveAction(true)
    }
  }

  const handleActionButtonClick = e => {
    e.preventDefault()
    props.actionButton.onClick({ keys: selectedKeys })
    setActiveAction(true)
  }

  const handlePagination = direction => {
    props.onPageChange()
    clearCheckedRows()
    if (direction === 'prev') {
      props.onPrev()
    } else {
      props.onNext()
    }
  }

  const handleRowClick = e => {
    if (
      e.target.tagName === 'TD' ||
      e.target.tagName === 'BUTTON' ||
      e.target.tagName === 'I' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'IMG'
    ) {
      return e
    }
    e.stopPropagation()
    return undefined
  }

  const tableClassNames = [
    midas.table,
    props.hover && midas.tableHover,
    props.striped && midas.tableStriped,
  ]
    .filter(val => val)
    .join(' ')

  const loadingOverlay = () => ({
    filter:
      activeAction &&
      ((props.actionModel && props.actionModel.loading) ||
        (props.actionButton && props.actionButton.loading)) &&
      'blur(1px)',
    opacity:
      activeAction &&
      ((props.actionModel && props.actionModel.loading) ||
        (props.actionButton && props.actionButton.loading)) &&
      '0.8',
  })

  const tableStyles = {
    whiteSpace: props.wrap && 'nowrap',
    tableLayout: props.fixed && 'fixed',
  }

  const tableHeaders = (
    <thead>
      <tr>
        {!props.disableRowActions && (
          <th style={{ width: props.fixed && '40px' }} key="mark-all">
            {(!props.fixedActionBar || props.showCheckboxInFixedActionBar) && (
              <label
                style={{ marginTop: '-1.3rem' }}
                className={midas.formCheckbox}
              >
                <input
                  type="checkbox"
                  checked={headerCheckbox === 'all'}
                  onChange={handleToggleAllRows}
                />
                <i className={midas.formIcon} />
              </label>
            )}
          </th>
        )}
        {props.headers.map(header => (
          <TableHeader
            key={header.id}
            header={header}
            lastSort={lastSort}
            setLastSort={setLastSort}
            onSort={props.onSort}
          />
        ))}
      </tr>
    </thead>
  )

  const searchField = (
    <form onSubmit={handleSearch}>
      <div className={midas.container}>
        <div className={midas.columns}>
          <div className={[midas.col7, midas.colXl6, midas.colMd0].join(' ')} />
          <div
            className={[midas.column, midas.col5, midas.colXl6, midas.colMd12]
              .filter(val => val)
              .join(' ')}
          >
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '.9rem',
              }}
              className={[midas.inputGroup].filter(val => val).join(' ')}
            >
              <input
                value={searchTerm}
                className={[midas.formInput, midas.inputSm]
                  .filter(val => val)
                  .join(' ')}
                placeholder={props.placeholder}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button
                type="submit"
                className={[
                  midas.button,
                  midas.buttonSmall,
                  midas.buttonPrimary,
                ]
                  .filter(val => val)
                  .join(' ')}
              >
                {props.searchTitle}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  )

  const disableActionButton = () =>
    checkedRows.length === 0 &&
    !(props.fixedActionBar && props.disableRowActions)

  const fixedActionBar = (
    <div
      style={{ ...loadingOverlay() }}
      className={{
        marginLeft: '-1rem',
        marginRight: '-1rem',
      }}
    >
      <div style={{ padding: '1rem', display: 'flex' }}>
        {props.actionButton && (
          <div style={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
            {!props.disableRowActions && !props.actionButton.hideCheckbox && (
              <label className={midas.formCheckbox} style={{ bottom: '.9rem' }}>
                <input
                  type="checkbox"
                  checked={headerCheckbox === 'all'}
                  onChange={handleToggleAllRows}
                />
                <i className={midas.formIcon} />
              </label>
            )}
            <button
              type="button"
              id="table-action-button"
              className={[
                midas.button,
                midas.buttonSmall,
                midas.buttonPrimary,
              ].join(' ')}
              disabled={disableActionButton()}
              onClick={e => handleActionButtonClick(e)}
            >
              {props.actionButton.label}
            </button>
            {!!props.actionButton.helpMessage && (
              <div style={{ marginLeft: '2rem' }}>
                <AppIcon help toolTipMessage={props.actionButton.helpMessage} />
              </div>
            )}
          </div>
        )}
        {props.search && (
          <div style={{ flexGrow: 2, alignItems: 'flex-end' }}>
            <form
              style={{ display: 'flex', gap: '.9rem' }}
              onSubmit={handleSearch}
            >
              <input
                className={styles.searchField}
                placeholder={props.placeholder}
                onChange={e => setSearchTerm(e.target.value)}
                value={searchTerm}
              />
              <button
                className={[
                  midas.button,
                  midas.buttonSmall,
                  midas.buttonPrimary,
                ].join(' ')}
                type="submit"
              >
                {props.searchTitle}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )

  const actions = (
    <div className={midas.container}>
      <div
        style={{ background: colors.gray, ...loadingOverlay() }}
        className={midas.columns}
      >
        <div className={[midas.column, midas.col4].join(' ')}>
          <div className={midas.inputGroup}>
            {props.actionButton && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {checkedRows.length > 0 && (
                  <div style={{ marginRight: '2rem' }}>
                    {getText(
                      'default.checkedRows',
                      checkedRows.length,
                      props.pagination.totalCount < props.pagination.rowsPerPage
                        ? props.pagination.totalCount
                        : props.pagination.rowsPerPage,
                    )}
                  </div>
                )}
                <button
                  type="button"
                  id="table-action-button"
                  className={[
                    midas.button,
                    midas.buttonSmall,
                    midas.buttonPrimary,
                  ].join(' ')}
                  onClick={e => handleActionButtonClick(e)}
                >
                  {props.actionButton.label}
                </button>
              </div>
            )}
            {props.actionModel && [
              <span
                key="action-model-label"
                style={{
                  color: 'white',
                  whiteSpace: 'nowrap',
                  padding: '.6rem .8rem 0 0',
                  fontSize: '1.1rem',
                }}
              >
                {props.actionModel.label}
              </span>,
              <select
                key="action-model-select"
                disabled={activeAction && props.actionModel.loading}
                id="table-actions"
                className={[midas.formSelect, midas.selectSm].join(' ')}
                defaultValue={selectedValue}
                onChange={e => handleActionSelectorChange(e)}
              >
                {props.actionModel.options.map(option => (
                  <option
                    key={option.value}
                    disabled={option.disabled}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>,
            ]}
          </div>
        </div>
      </div>
    </div>
  )

  const rows = tbody => {
    if (tbody.type !== 'tbody') {
      return new Error(
        'Midas Error: Data Table requires parent child node to be of type <tbody />',
      )
    }

    return (
      <tbody>
        {!tbody.props.children.length && props.noContentMsg && (
          <tr
            key={Math.random()}
            style={{ height: '100px', textAlign: 'center', color: '#999' }}
          >
            <td colSpan={props.headers.length + 1}>{props.noContentMsg}</td>
          </tr>
        )}
        {tbody.props.children.map((tr, index) => (
          <tr
            {...tr.props}
            key={tr.key}
            onClickCapture={e => handleRowClick(e)}
            style={{
              cursor: props.hover ? 'pointer' : '',
              background: checkedRows.includes(index) && colors.blue5,
              ...tr.props.style,
            }}
          >
            {!props.disableRowActions && (
              <td
                style={{
                  width: '.5rem',
                }}
                onClick={e => {
                  e.stopPropagation()
                  if (tr.props['data-checkbox-disabled'] === 'false') {
                    handleRowSelect(index, tr.key)
                  }
                }}
              >
                {/* eslint-disable */
                      /* Preventing event bubbling */}
                    <label
                        style={{marginTop: '-1.3rem'}}
                        className={midas.formCheckbox}
                        onClick={e => e.stopPropagation()}
                    >
                      {/* eslint-disable */}
                      <input
                          disabled={
                              tr.props.disabled ||
                              tr.props['data-checkbox-disabled'] === 'true'
                          }
                          type="checkbox"
                          checked={checkedRows.includes(index)}
                          onChange={e => {
                            e.stopPropagation()
                            handleRowSelect(index, tr.key)
                          }}
                      />
                      <i className={midas.formIcon}/>
                    </label>
                  </td>
              )}
              {tr.props.children.map((td, i) => {
                if (td.props) {
                  const style = Object.assign({}, td.props.style, {
                    overflow: props.fixed && 'hidden',
                    textOverflow: props.fixed && 'ellipsis',
                  })

                  return (
                      /* eslint-disable */
                      <td
                          {...td.props}
                          key={`${td.type} ${i}`}
                          style={{...style}}
                      >
                        {td.props.children}
                      </td>
                      /* eslint-enable */
                )
              }
            })}
          </tr>
        ))}
      </tbody>
    )
  }

  const _pagination = !props.disablePagination && (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        ...loadingOverlay(),
      }}
    >
      <div style={{ padding: '.8rem', display: 'flex', alignItems: 'center' }}>
        <span
          style={{
            whiteSpace: 'nowrap',
            fontSize: '1.1rem',
            padding: '0 1rem 0 0',
          }}
        >
          {getText('default.rowsPerPage')}
        </span>
        <select
          id="table-actions"
          className={[midas.formSelect, midas.selectSm].join(' ')}
          value={props.pagination.rowsPerPage}
          onChange={e => {
            clearCheckedRows()
            props.onRowsPerPageChange(e.target.value)
          }}
        >
          {listSize.map(item => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginLeft: 'auto' }}>
        <span style={{ padding: '0 1rem', color: '#999' }}>
          {props.pagination.currentPage}
          {' / '}
          {props.pagination.totalPages}
        </span>
        <button
          type="button"
          className={classNames(midas.hand, styles.previousPageButton)}
          onClick={() => handlePagination('prev')}
        >
          <svg fillRule="evenodd" height={12} width={8} viewBox="0 0 8 12">
            <path d="M7.5 10.6L2.8 6l4.7-4.6L6.1 0 0 6l6.1 6z" />
          </svg>
        </button>
        <button
          type="button"
          className={classNames(midas.hand, styles.nextPageButton)}
          onClick={() => handlePagination('next')}
        >
          <svg fillRule="evenodd" height={12} width={8} viewBox="0 0 8 12">
            <path d="M0 10.6L4.7 6 0 1.4 1.4 0l6.1 6-6.1 6z" />
          </svg>
        </button>
      </div>
    </div>
  )

  useEffect(() => setSearchTerm(props.searchTerm), [props.searchTerm])

  useEffect(() => {
    props.onCheckedRowsChange(checkedRows?.length)
  }, [checkedRows])

  useEffect(() => {
    if (
      activeAction &&
      ((props.actionModel && !props.actionModel.loading) ||
        (props.actionButton && !props.actionButton.loading))
    ) {
      clearCheckedRows()
      if (props.actionModel) {
        props.actionModel.callbackOnClear()
      }
      if (props.actionButton) {
        props.actionButton.callbackOnClear()
      }
      setActiveAction(false)
    }
  }, [props.actionModel, props.actionButton])

  useEffect(() => {
    clearCheckedRows()
    props.onSort(lastSort.headerName, lastSort.direction)
  }, [lastSort])

  const actionBar = fixed => {
    if (fixed) {
      return fixedActionBar
    }
    if (checkedRows.length > 0) {
      return actions
    }

    return props.search && searchField
  }

  return (
    <div style={{ position: 'relative' }}>
      {actionBar(props.fixedActionBar)}
      <table
        style={{ ...tableStyles, ...loadingOverlay(), position: 'relative' }}
        className={tableClassNames}
        data-cy={props.dataCy}
      >
        {tableHeaders}
        {rows(props.children)}
      </table>
      {_pagination}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          display:
            activeAction &&
            ((props.actionModel && props.actionModel.loading) ||
              (props.actionButton && props.actionButton.loading))
              ? 'block'
              : 'none',
        }}
      >
        <div
          className={
            activeAction &&
            ((props.actionModel && props.actionModel.loading) ||
              (props.actionButton && props.actionButton.loading))
              ? midas.loading
              : undefined
          }
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: '10',
          }}
        />
      </div>
    </div>
  )
}

DataTable.propTypes = {
  children: PropTypes.node,
  noContentMsg: PropTypes.string,
  striped: PropTypes.bool,
  hover: PropTypes.bool,
  wrap: PropTypes.bool,
  fixed: PropTypes.bool,
  search: PropTypes.bool,
  disableRowActions: PropTypes.bool,
  disablePagination: PropTypes.bool,

  placeholder: props => {
    if (props.placeholder && !props.search) {
      return new Error(
        'Midas Error: Data Table requires search to be true if a placeholder is set',
      )
    }
    return undefined
  },
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      color: PropTypes.string,
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      nonSortable: PropTypes.bool,
      hidden: PropTypes.bool,
    }),
  ).isRequired,
  pagination: PropTypes.shape({
    rowsPerPage: PropTypes.number,
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    totalCount: PropTypes.number,
  }),
  searchTerm: PropTypes.string,
  onSearch: PropTypes.func,
  onPageChange: PropTypes.func,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  lastSort: PropTypes.shape({
    headerName: PropTypes.string,
    direction: PropTypes.string,
  }),
  onSort: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  actionModel: PropTypes.shape({
    label: PropTypes.string,
    default: PropTypes.string,
    loading: PropTypes.bool,
    callbackOnClear: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
        disabled: PropTypes.bool,
        default: PropTypes.bool,
      }),
    ),
  }),
  actionButton: PropTypes.shape({
    label: PropTypes.string,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    callbackOnClear: PropTypes.func,
    hideCheckbox: PropTypes.bool,
    helpMessage: PropTypes.string,
  }),
  onActionSelectorChange: PropTypes.func,
  dataCy: PropTypes.string,
  fixedActionBar: PropTypes.bool,
  onCheckedRowsChange: PropTypes.func,
  showCheckboxInFixedActionBar: PropTypes.bool,
}
DataTable.defaultProps = {
  children: null,
  noContentMsg: '',
  pagination: {
    rowsPerPage: 10,
    currentPage: 1,
    totalPages: null,
    totalCount: null,
  },
  striped: false,
  hover: false,
  wrap: false,
  fixed: false,
  search: false,
  disableRowActions: false,
  disablePagination: false,
  searchTerm: '',
  placeholder: '',
  lastSort: {
    headerName: '',
    direction: 'asc',
  },
  actionModel: null,
  actionButton: null,
  dataCy: null,
  fixedActionBar: false,
  onSearch: () => {},
  onPageChange: () => {},
  onPrev: () => {},
  onNext: () => {},
  onSort: () => {},
  onRowsPerPageChange: () => {},
  onActionSelectorChange: () => {},
  onCheckedRowsChange: () => {},
  showCheckboxInFixedActionBar: false,
}

export default DataTable
